import { SITE_URL } from '@/consts/environmental';
import { type Visit } from '@/services/visitsService/types';

import expressCurologySignUpSkinConfig from './config.json';
import {
  type ExpressCurologySignUpSkinConfigType,
  type ExpressCurologySignUpSkinPdpSourceConfigType,
} from './types';

export const allArrayValuesInOtherArray = (
  subsetArray: any[],
  parentArray: any[],
) => {
  return subsetArray.every((value) => parentArray.includes(value));
};

export const getBundleConfig = (visit: Visit) => {
  const redirectUrl = SITE_URL + '/shop/shop-all';

  const pdpSource = visit?.sessionBag?.searchParams?.pdpSource;

  if (!pdpSource) {
    return {
      bundleConfig: null,
      redirectUrl,
    };
  }

  const typedExpressCurologySignUpSkinConfig =
    expressCurologySignUpSkinConfig as ExpressCurologySignUpSkinConfigType;

  if (!typedExpressCurologySignUpSkinConfig.pdpSources[pdpSource]) {
    return {
      bundleConfig: null,
      redirectUrl,
    };
  }

  return {
    bundleConfig: typedExpressCurologySignUpSkinConfig.pdpSources[pdpSource],
    redirectUrl: null,
  };
};

export const getFormattedPrice = (
  amount: number,
  freeCopy: string = 'Free',
) => {
  if (amount === 0) {
    return freeCopy;
  }

  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(amount / 100);
};

export const getNextBoxTotals = (
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType,
  selectedAddOnProductItemIds: number[],
) => {
  let nextBoxTotal = 0;
  let nextBoxTotalPerMonth = 0;

  const { addOnProducts, nextBoxMultiRxDiscountAmount, setProducts } =
    bundleConfig;

  setProducts.forEach((setProduct) => {
    nextBoxTotal += setProduct.fullSizePrice;
  });

  if (addOnProducts) {
    addOnProducts.forEach((addOnProduct) => {
      if (
        allArrayValuesInOtherArray(
          addOnProduct.itemIds,
          selectedAddOnProductItemIds,
        )
      ) {
        nextBoxTotal += addOnProduct.fullSizePrice;
      }
    });
  }

  nextBoxTotal -= nextBoxMultiRxDiscountAmount;

  nextBoxTotalPerMonth = nextBoxTotal / 2;

  return {
    nextBoxTotal,
    nextBoxTotalPerMonth,
  };
};

export const getTotal = (
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType,
  selectedAddOnProductItemIds: number[],
) => {
  let total = 0;

  const {
    addOnProducts,
    dermatologyConsultationPrice,
    setProducts,
    shippingPrice,
  } = bundleConfig;

  setProducts.forEach((setProduct) => {
    setProduct.priceSummaryItems.forEach((priceSummaryItem) => {
      total += priceSummaryItem.price.final;
    });
  });

  if (addOnProducts) {
    addOnProducts.forEach((addOnProduct) => {
      if (
        allArrayValuesInOtherArray(
          addOnProduct.itemIds,
          selectedAddOnProductItemIds,
        )
      ) {
        addOnProduct.priceSummaryItems.forEach((priceSummaryItem) => {
          total += priceSummaryItem.price.final;
        });
      }
    });
  }

  total += shippingPrice;
  total += dermatologyConsultationPrice;

  return total;
};

export const getTotalWithoutDiscount = (
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType,
  selectedAddOnProductItemIds: number[],
) => {
  let totalWithoutDiscount = 0;

  const {
    addOnProducts,
    dermatologyConsultationPrice,
    setProducts,
    shippingPrice,
  } = bundleConfig;

  setProducts.forEach((setProduct) => {
    setProduct.priceSummaryItems.forEach((priceSummaryItem) => {
      totalWithoutDiscount += priceSummaryItem.price.original;
    });
  });

  if (addOnProducts) {
    addOnProducts.forEach((addOnProduct) => {
      if (
        allArrayValuesInOtherArray(
          addOnProduct.itemIds,
          selectedAddOnProductItemIds,
        )
      ) {
        addOnProduct.priceSummaryItems.forEach((priceSummaryItem) => {
          totalWithoutDiscount += priceSummaryItem.price.original;
        });
      }
    });
  }

  totalWithoutDiscount += shippingPrice;
  totalWithoutDiscount += dermatologyConsultationPrice;

  return totalWithoutDiscount;
};
